<template>
  <v-app>
    <!-- Maintenance Mode Overlay -->
    <v-overlay
      v-model="showMaintenance"
      persistent
      class="align-center justify-center text-center bg-white"
      style="opacity: 1; z-index: 9999"
    >
      <v-container>
        <v-img
          :src="
            settingsStore.logoPreview ||
            'https://cdn-icons-png.flaticon.com/512/2596/2596956.png'
          "
          width="150"
          class="mx-auto mb-6"
        ></v-img>
        <h1 class="text-h3 font-weight-black mb-4 text-primary">
          {{ $t("maintenance.title") }}
        </h1>
        <p class="text-h6 text-grey-darken-1 mb-6">
          {{ $t("maintenance.subtitle") }}
        </p>
        <v-btn
          color="primary"
          variant="tonal"
          prepend-icon="mdi-phone"
          class="font-weight-bold"
          :href="'tel:' + settingsStore.phone"
          v-if="settingsStore.phone"
        >
          {{ $t("maintenance.contact_us", { phone: settingsStore.phone }) }}
        </v-btn>
      </v-container>
    </v-overlay>

    <component :is="currentLayout" v-if="!showMaintenance || isAdmin">
      <router-view v-slot="{ Component }">
        <v-fade-transition mode="out-in">
          <component :is="Component" />
        </v-fade-transition>
      </router-view>

      <v-snackbar
        v-model="bar"
        location="top center"
        :color="snackColor"
        elevation="24"
        rounded="pill"
        class="mb-6"
        min-width="200"
        :timeout="3000"
      >
        <div class="d-flex align-center gap-2">
          <v-icon v-if="snackIcon" size="20" color="white">{{
            snackIcon
          }}</v-icon>
          <span class="font-weight-bold">{{ itemTitle }}</span>
        </div>
        <template v-slot:actions>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            color="white"
            @click="bar = false"
          ></v-btn>
        </template>
      </v-snackbar>
    </component>
  </v-app>
</template>

<script setup>
import { onMounted, ref, inject, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useLocale, useTheme } from "vuetify";
import { useSettingsStore } from "@/store/Settings";
import { useAuthStore } from "@/store/auth/LogIn";

import LayOut from "@/components/LayOut.vue";
import DashboardLayout from "@/components/DashboardLayout.vue";
import GuestLayout from "@/components/GuestLayout.vue";

import { AddInCart } from "@/store/Cart";
import { AddFavProduct } from "@/store/Favourate";

const route = useRoute();
const emitter = inject("emitter");
const bar = ref(false);
const itemTitle = ref("");
const snackColor = ref("primary");
const snackIcon = ref("");

const cartStore = AddInCart();
const favStore = AddFavProduct();
const settingsStore = useSettingsStore();
const authStore = useAuthStore();

const { locale: i18nLocale } = useI18n();
const { current: vuetifyLocale } = useLocale();
const theme = useTheme();

// Derived from auth and settings
const isAdmin = computed(() =>
  ["admin", "moderator"].includes(authStore.currentUser?.role)
);
const showMaintenance = computed(() => {
  // Only show maintenance if mode is active and user is NOT an admin
  return (
    settingsStore.maintenanceMode &&
    !isAdmin.value &&
    route.path !== "/Auth/LogIn"
  );
});

watch(
  i18nLocale,
  (newLocale) => {
    vuetifyLocale.value = newLocale;
    document.dir = newLocale === "ar" ? "rtl" : "ltr";
  },
  { immediate: true }
);

const layouts = {
  LayOut,
  DashboardLayout,
  GuestLayout,
};

const currentLayoutName = computed(() => {
  return route.meta.layout || "LayOut";
});

const currentLayout = computed(() => {
  return layouts[currentLayoutName.value];
});

watch(currentLayoutName, () => {
  applyTheme(settingsStore.$state);
});

// Watch for store changes (like after a fetch) and apply theme reactively
watch(
  () => settingsStore.primaryColor,
  () => {
    applyTheme(settingsStore.$state);
  }
);

onMounted(async () => {
  emitter.on("showMessage", (data) => {
    if (typeof data === "string") {
      itemTitle.value = data;
      snackColor.value = "primary";
      snackIcon.value = "mdi-information";
    } else {
      itemTitle.value = data.text || "";
      snackColor.value = data.color || "primary";
      snackIcon.value =
        data.icon ||
        (data.color === "success"
          ? "mdi-check-circle"
          : data.color === "error"
          ? "mdi-alert-circle"
          : "mdi-information");
    }
    bar.value = true;
  });

  // Persist cart and favorites
  if (authStore.isAuthenticated) {
    cartStore.fetchCart();
    favStore.fetchFavorites();
  }

  // Centralized Settings & Theme Application
  try {
    const data = await settingsStore.fetchSettings();
    applyTheme(data);
  } catch (err) {
    console.error("Failed to load settings:", err);
  }
});

const hexToRgb = (hex) => {
  if (!hex || hex.length < 7) return "0,0,0";
  let r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
};

const applyTheme = (data) => {
  if (!data) return;

  if (data.primaryColor) {
    const pColor = data.primaryColor;
    theme.themes.value.orangeTheme.colors = {
      ...theme.themes.value.orangeTheme.colors,
      primary: pColor,
      secondary: pColor,
    };
    theme.global.name.value = "orangeTheme";
    const pRgb = hexToRgb(pColor);
    document.documentElement.style.setProperty("--v-theme-primary", pRgb);
    document.documentElement.style.setProperty("--v-theme-secondary", pRgb);
    document.body.style.setProperty("--v-theme-primary", pRgb);
    document.body.style.setProperty("--v-theme-secondary", pRgb);
  }
};
</script>

<style lang="scss">
/* نفس الـ CSS بتاعك بدون أي تغيير */
/* Theme Overrides for hardcoded orange colors */
.bg-primary,
.bg-orange,
.v-btn.bg-orange,
.v-btn.text-orange {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
}

.text-primary,
.text-orange,
.text-orange-darken-2,
.v-icon.text-orange {
  color: rgb(var(--v-theme-primary)) !important;
}

.border-primary,
.border-orange {
  border-color: rgb(var(--v-theme-primary)) !important;
}

/* Ensure the wrapper respects the theme background */
.v-application {
  background-color: rgb(var(--v-theme-background)) !important;
}

#app {
  font-family: "Cairo", "Inter", "Tajawal", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Fluid Typography System */
:root {
  --step--2: clamp(0.61rem, calc(0.58rem + 0.13vw), 0.69rem);
  --step--1: clamp(0.73rem, calc(0.67rem + 0.28vw), 0.9rem);
  --step-0: clamp(0.88rem, calc(0.77rem + 0.5vw), 1.15rem);
  --step-1: clamp(1.05rem, calc(0.88rem + 0.83vw), 1.5rem);
  --step-2: clamp(1.26rem, calc(1.01rem + 1.25vw), 1.9rem);
  --step-3: clamp(1.51rem, calc(1.15rem + 1.83vw), 2.5rem);

  /* Global Spacing Scale - Compacted for better rhythm */
  --v-section-margin: clamp(20px, 4vw, 50px);
  --v-content-gap: clamp(12px, 2vw, 24px);
}

html {
  scroll-behavior: smooth;
}

body {
  overflow-x: hidden;
  font-family: inherit;
  background-color: rgb(var(--v-theme-background));
  line-height: 1.6;
}

.text-h3 {
  font-size: var(--step-3) !important;
}
.text-h4 {
  font-size: var(--step-2) !important;
}
.text-h5 {
  font-size: var(--step-1) !important;
}
.text-h6 {
  font-size: var(--step-0) !important;
}
nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: rgba(var(--v-theme-on-surface), 0.8);

    &.router-link-exact-active {
      color: rgb(var(--v-theme-primary));
    }
  }
}
.nav-text {
  font-size: 0.8rem !important;
}
/* Shared Section Header Redesign */
.custom-section-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 40px;
}

.custom-badge {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
  color: rgb(var(--v-theme-primary)) !important;
  font-size: 0.9rem !important;
  letter-spacing: 0.5px;
  font-weight: 700;
  padding: 0 24px;
  margin-bottom: 16px;
}

.custom-main-title {
  font-size: clamp(1.8rem, 3.5vw, 2.8rem) !important;
  color: rgb(var(--v-theme-primary));
  font-weight: 900 !important;
  margin-bottom: 8px;
  line-height: 1.2;
}

.custom-divider {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
}

.custom-divider .bar {
  height: 6px;
  background-color: rgb(var(--v-theme-primary));
  border-radius: 10px;
  display: inline-block;
}

.custom-divider .bar.long {
  width: 100px;
}

.custom-divider .bar.short {
  width: 25px;
}

@media (max-width: 600px) {
  .custom-main-title {
    font-size: 2rem !important;
  }
  .custom-divider .bar.long {
    width: 60px;
  }
  .custom-divider .bar.short {
    width: 15px;
  }
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Dashboard Specific Typography Refinement */
.dashboard-layout {
  font-size: 0.88rem !important;

  :deep(.v-list-item-title),
  :deep(.v-list-item-subtitle),
  :deep(.v-btn__content),
  :deep(.v-label),
  :deep(.v-field__input),
  :deep(table td),
  :deep(table th) {
    font-size: 0.88rem !important;
  }

  :deep(.v-card-title),
  :deep(.v-toolbar-title),
  :deep(.v-list-subheader) {
    font-size: 1rem !important;
    font-weight: 700 !important;
  }

  :deep(.text-h4),
  :deep(.text-h5),
  :deep(.text-h6) {
    font-size: 1.15rem !important;
    font-weight: 800 !important;
  }

  /* Compact spacing for buttons in dashboard */
  :deep(.v-btn) {
    letter-spacing: 0;
    font-weight: 600;
  }
}
</style>
